import { z } from "zod";
import { formConfigToZodSchemas, getFormConfigDefaultValues } from "../form-config-to-zod-schema";
import {
  getFormKeys,
  type FormConfig,
  type FormConfigInPages,
  type I18NFunction,
  type SectionConfig,
} from "../form-config-types";
import {
  mapToNumPatientsEnumLabels,
  mapToStandardPercentageEnumLabels,
  mapToTypesOfMeetingFrequencyEnumLabels,
  mapToTypesOfSpecialistsEnumLabels,
  mapToYesNoEnumLabels,
  numPatientsEnum,
  standardPercentageEnum,
  typesOfMeetingFrequencyEnum,
  typesOfSpecialistsEnum,
  yesNoEnum,
} from "./form-data-enums";
import { GuidelineLink } from "~/components/GuidelineDialog";
import { enumErrorMap } from "./form-data-utils";
// import { GuidelineLink } from "~/components/GuidelineDialog";

const confirmatoryDiagnosisFormConfigInPages = (l: I18NFunction) =>
  [
    // page 1
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        areThereSharedCharacterists: {
          type: "textarea",
          label: l(
            "1. Are there shared characteristics among your advanced NMSC (BSCC/BCC) referrals? Do they have similar backgrounds (eg. profession, town, underlying conditions)?",
            "1. Y a-t-il des caractéristiques partagées chez les patients atteints de NMSC (CSCC/BCC) de stade avancé qui vous sont référés? Présente-il des antécédents similaires (emplois, villes, affections sous jacentes)?",
          ),
          schema: z.string({ invalid_type_error: l("Please enter a value", "Ce champ est requis") }).min(1, {
            message: l("Please enter a value", "Ce champ est requis"),
          }),
        },
        // 2.2 Where do your referrals come from? Please specify % from each specialty
        whereDoYourReferralsComeFrom: {
          type: "object",
          label: l(
            "2. Where do your referrals come from? Please specify % from each specialty",
            "2. D'où proviennent les patients NMSC qui vous sont référés? Veuillez préciser la part en % pour chaque spécialité",
          ),
          offerNotApplicableForEntireObject: true,
          properties: {
            /**
             *
             * THis isn't really working... need to add the optional stuff
             * but still not ideal. Plus i need compound hide/require rules... which is not great
             */
            // notApplicable: {
            //   type: "singleCheckbox",
            //   label: l("Not Applicable", "Non applicable"),
            //   schema: z.boolean(),
            // },
            gpPcp: {
              type: "radioGroup",
              label: l("Primary Care Physician", "Médecins généralistes"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            rad_onc: {
              type: "radioGroup",
              label: l("Radiation oncologist", "Radio-oncologues"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            dermatologist: {
              type: "radioGroup",
              label: l("Dermatologist", "Dermatologues"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            surgeon: {
              type: "radioGroup",
              label: l("Surgical oncologist", "Oncologues chirurgicaux"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            ent: {
              type: "radioGroup",
              label: l("ENT", "ORL"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            other: {
              type: "radioGroup",
              label: l("Others", "Autres"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            otherSpecify: {
              type: "textarea",
              label: l("Others (please specify)", "Autres (Veuillez préciser)"),
              schema: z.string().nullish(),
              dependencies: [
                {
                  if: ["whereDoYourReferralsComeFrom.other", "equals", "0"],
                  or: ["whereDoYourReferralsComeFrom.other", "equals", undefined],
                  or2: ["whereDoYourReferralsComeFrom.other", "equals", null],
                  then: "hide",
                },
                {
                  if: ["whereDoYourReferralsComeFrom.other", "not-equals", "0"],
                  and: ["whereDoYourReferralsComeFrom.other", "not-equals", undefined],
                  and2: ["whereDoYourReferralsComeFrom.other", "not-equals", null],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 2.2 What percentage of your patients are NMSC (CSCC/BCC) patients?
        whatPercentageOfYourPatientsAreNMSC: {
          type: "radioGroup",
          label: l(
            "3. What percentage of your patients are NMSC (CSCC/BCC) patients?",
            "3. Quel pourcentage de vos patients sont atteints de NMSC (CSCC/BCC)?",
          ),
          schema: z.enum(standardPercentageEnum, {
            errorMap: enumErrorMap(l),
          }),
          mapToLabels: mapToStandardPercentageEnumLabels,
        },
        // 2.3 On average, in one month, how many CSCC patients do you personally see in your practice?
        howManyCSCCPatientsDoYouSeeInYourPractice: {
          type: "object",
          label: "",
          properties: {
            howMany: {
              type: "radioGroup",
              label: l(
                "4. On average, in one month, how many CSCC patients do you personally see in your practice?",
                "4. En moyenne, en un mois, combien de patients atteints de CSCC voyez-vous personnellement dans votre pratique?",
              ),
              schema: z.enum(numPatientsEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToNumPatientsEnumLabels,
            },
            pleaseSpecify: {
              type: "textarea",
              label: l("Please specify how many", "Veuillez préciser"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["howManyCSCCPatientsDoYouSeeInYourPractice.howMany", "not-equals", "50_plus"],
                  then: "hide",
                },
                {
                  if: ["howManyCSCCPatientsDoYouSeeInYourPractice.howMany", "equals", "50_plus"],
                  then: "require",
                },
              ],
            },
          },
        },
        // 2.4 On average, in one month, how many BCC patients do you personally see in your practice?
        howManyBCCPatientsDoYouSeeInYourPractice: {
          type: "object",
          label: "",
          properties: {
            howMany: {
              type: "radioGroup",
              label: l(
                "5. On average, in one month, how many BCC patients do you personally see in your practice?",
                "5. En moyenne, en un mois, combien de patients atteints de BCC voyez-vous personnellement dans votre pratique?",
              ),
              schema: z.enum(numPatientsEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToNumPatientsEnumLabels,
            },
            pleaseSpecify: {
              type: "textarea",
              label: l("Please specify how many", "Veuillez préciser"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["howManyBCCPatientsDoYouSeeInYourPractice.howMany", "not-equals", "50_plus"],
                  then: "hide",
                },
                {
                  if: ["howManyBCCPatientsDoYouSeeInYourPractice.howMany", "equals", "50_plus"],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 2.5 What proportion of CSCC patients are: (provide % for each group)
        whatProportionOfCSCCPatientsAre: {
          type: "object",
          label: l(
            "6a. What proportion of CSCC patients are: (provide % for each group)",
            "6a. Quelle proportion de patients atteints de CSCC sont : (indiquer le pourcentage pour chaque groupe)",
          ),
          properties: {
            // 1- High risk of recurrence following resection (early stage)
            highRiskOfRecurrenceFollowingResection: {
              type: "radioGroup",
              label: l(
                "High-risk of recurrence following resection (early stage)",
                "Risque élevé de récidive après résection (stade précoce)",
              ),
              schema: z.enum(standardPercentageEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            // 2- Locally advanced
            locallyAdvanced: {
              type: "radioGroup",
              label: l("Locally advanced", "Localement avancé"),
              schema: z.enum(standardPercentageEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            // 3- Metastatic (CSCC)
            metastatic: {
              type: "radioGroup",
              label: l("Metastatic (CSCC)", "Métastatique (CSCC)"),
              schema: z.enum(standardPercentageEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 2.5 What proportion of BCC patients are: (provide % for each group)
        whatProportionOfBCCPatientsAre: {
          type: "object",
          label: l(
            "6b. What proportion of BCC patients are: (provide % for each group)",
            "6b. Quelle proportion de patients atteints de BCC sont : (indiquer le pourcentage pour chaque groupe)",
          ),
          properties: {
            // 1- High risk of recurrence following resection (early stage)
            highRiskOfRecurrenceFollowingResection: {
              type: "radioGroup",
              label: l(
                "High-risk of recurrence following resection (early stage)",
                "Risque élevé de récidive après résection (stade précoce)",
              ),
              schema: z.enum(standardPercentageEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            // 2- Locally advanced
            locallyAdvanced: {
              type: "radioGroup",
              label: l("Locally advanced", "Localement avancé"),
              schema: z.enum(standardPercentageEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
            // 3- Metastatic (BCC)
            metastatic: {
              type: "radioGroup",
              label: l("Metastatic (BCC)", "Métastatique (BCC)"),
              schema: z.enum(standardPercentageEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToStandardPercentageEnumLabels,
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "Guidelines",
      pageDescription: "",
      formFields: {
        guidelinesReferredTo: {
          type: "object",
          label: "",
          properties: {
            // 2.6 When considering available guidelines, which ones do you typically refer to for the management of high risk or advanced NMSC? [open box]
            whichGuidelinesDoYouReferToForManagementOfHighRiskOrAdvancedNMSC: {
              type: "textarea",
              // label: l(
              //   "7. When considering available guidelines, which ones do you typically refer to for the management of high-risk or advanced NMSC (CSCC/BCC)?",
              //   "7. Lorsque vous considérez les lignes directrices disponibles, lesquelles utilisez-vous généralement pour la prise en charge des NMSC (CSCC/BCC) à risque élevé ou avancés?"
              // ),
              label: (
                <p className="nowrap">
                  {l("7. When considering available", "7. Lorsque vous considérez les")}{" "}
                  <GuidelineLink>{l("guidelines,", "lignes directrices,")}</GuidelineLink>{" "}
                  {l(
                    "which ones do you typically refer to for the management of high-risk or advanced NMSC (CSCC/BCC)?",
                    "disponibles, lesquelles utilisez-vous généralement pour la prise en charge des NMSC (CSCC/BCC) à risque élevé ou avancés?",
                  )}
                </p>
              ),
              stringSafeFallback: l(
                "7. When considering available guidelines which ones do you typically refer to for the management of high-risk or advanced NMSC (CSCC/BCC)?",
                "7. Lorsque vous considérez les lignes directrices, disponibles, lesquelles utilisez-vous généralement pour la prise en charge des NMSC (CSCC/BCC) à risque élevé ou avancés?",
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .min(1, {
                  message: l("Please enter a value", "Ce champ est requis"),
                }),
            },
            // 2.7 Are there Canadian resources you refer to? [open box]
            areThereCanadianResourcesYouReferTo: {
              type: "radioGroup",
              label: l(
                "8. Are there Canadian resources you refer to?",
                "8. Existe-t-il des ressources canadiennes que vous utilisez?",
              ),
              schema: z.enum(yesNoEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToYesNoEnumLabels,
              cols: 2,
            },
            pleaseGiveDetails: {
              type: "textarea",
              label: l("Please give details.", "Veuillez décrire."),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "guidelinesReferredTo.areThereCanadianResourcesYouReferTo",
                    "not-contains-case-insensitive",
                    "Yes",
                  ],
                  then: "hide",
                },
                {
                  if: ["guidelinesReferredTo.areThereCanadianResourcesYouReferTo", "contains-case-insensitive", "Yes"],
                  then: "require",
                },
              ],
            },
          },
        },

        // 2.8 In your opinion, what are the gaps in the current resources available and what would help to address these gaps? [open box]
        whatAreTheGapsInTheCurrentResourcesAvailableAndWhatWouldHelpToAddressTheseGaps: {
          type: "textarea",
          label: l(
            "9. In your opinion, what are the gaps in the current resources available and what would help to address these gaps?",
            "9. À votre avis, quelles sont les lacunes dans les ressources actuellement disponibles et qu'est-ce qui pourrait aider à les combler?",
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, {
              message: l("Please enter a value", "Ce champ est requis"),
            }),
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        guidelinesForHighRisk: {
          type: "object",
          label: "",
          properties: {
            // 2.9 How do you define and assess 'high-risk' when considering risk of recurrence? [open box]
            howDoYouDefineAndAssessHighRiskWhenConsideringRiskOfRecurrence: {
              type: "textarea",
              label: l(
                "10. How do you define and assess 'high-risk' when considering risk of recurrence?",
                "10. Comment définissez-vous et évaluez-vous un « risque élevé »?",
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .min(1, {
                  message: l("Please enter a value", "Ce champ est requis"),
                }),
            },
            // 2.11 Is the definition universally accepted:
            inYourRegion: {
              type: "radioGroup",
              label: l(
                "11 Is the definition of 'high-risk' universally accepted in your region?",
                "11 La définition de « risque élevé » est-elle universellement acceptée dans votre province?",
              ),
              schema: z.enum(yesNoEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToYesNoEnumLabels,
              cols: 2,
            },
            inCanada: {
              type: "radioGroup",
              label: l(
                "Is the definition of 'high-risk' universally accepted in Canada?",
                "La définition de « risque élevé » est-elle universellement acceptée au Canada?",
              ),
              schema: z.enum(yesNoEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToYesNoEnumLabels,
              cols: 2,
            },
            pleaseExplain: {
              type: "textarea",
              label: l("Please explain", "Veuillez expliquer"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .min(1, {
                  message: l("Please enter a value", "Ce champ est requis"),
                }),
            },
            // 2.10 Do you have a preferred guideline for reference? [open box]
            doYouHaveAPreferredGuidelineForReference: {
              type: "textarea",
              label: l(
                "12. Do you have a preferred guideline for reference?",
                "12. Vous référez-vous à une ligne directrice en particulier?",
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .min(1, {
                  message: l("Please enter a value", "Ce champ est requis"),
                }),
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "Multidisciplinary team (MDT)",
      pageDescription: "",
      formFields: {
        // 2.12 Who do you typically communicate most with regarding your advanced/metastatic patients? [open box]
        whoDoYouTypicallyCommunicateMostWithRegardingYourAdvancedMetastaticPatients: {
          type: "textarea",
          label: l(
            "13. Who do you typically communicate/consult with regarding your locally advanced/metastatic patients?",
            "13. Avec qui communiquez-vous et qui consultez-vous concernant vos patients NMSC localement avancés/métastatiques?",
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, {
              message: l("Please enter a value", "Ce champ est requis"),
            }),
        },
        // 2.13 Who do you typically communicate most with regarding your high-risk patients? [open box]
        whoDoYouTypicallyCommunicateMostWithRegardingYourHighRiskPatients: {
          type: "textarea",
          label: l(
            "14. Who do you typically communicate/consult with regarding your high-risk patients (patient's at a high-risk of recurrence)?",
            "14. Avec qui communiquez-vous et qui consultez-vous concernant vos patients NMSC à risque élevé de récidive?",
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, {
              message: l("Please enter a value", "Ce champ est requis"),
            }),
        },
        mdtClinic: {
          type: "object",
          label: "",
          properties: {
            // 2.14 Does your center have a MDT clinic? [Yes or No] [If selected No, skip to question 2.17]
            // [If selected yes] Which specialists are part of this MDT clinic?
            // Dermatologists [checkbox]
            // Surgeons [checkbox]
            // Radio oncologists [checkbox]
            // Pathologists [checkbox]
            // Community medical oncologists [checkbox]
            // Others (please specify) [open box]
            doesYourCenterHaveAMDTClinic: {
              type: "radioGroup",
              label: l(
                "15. Does your center have an MDT clinic?",
                "15. Votre centre dispose-t-il d'une clinique dotée d'une EMD?",
              ),
              schema: z.enum(yesNoEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToYesNoEnumLabels,
              cols: 2,
            },
            whichSpecialistsArePartOfThisMDTClinic: {
              type: "checkboxGroup",
              label: l(
                "Which specialists are part of this MDT clinic?",
                "Quels spécialistes font partie de cette EMD?",
              ),
              schema: z
                .array(z.enum(typesOfSpecialistsEnum), {
                  required_error: l("Please select an option", "Veuillez sélectionner une option"),
                  invalid_type_error: l("Please select an option", "Veuillez sélectionner une option"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "not-equals", "Yes"],
                  then: "hide",
                },
                {
                  if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "equals", "Yes"],
                  then: "require",
                },
              ],
              mapToLabels: mapToTypesOfSpecialistsEnumLabels,
            },
            whichSpecialistsArePartOfThisMDTClinicOther: {
              type: "textarea",
              label: l("Please specify 'Others':", "Autres (veuillez préciser) :"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "not-equals", "Yes"],
                  or: ["mdtClinic.whichSpecialistsArePartOfThisMDTClinic", "not-contains-case-insensitive", "Others"],
                  then: "hide",
                },
                {
                  if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "equals", "Yes"],
                  and: ["mdtClinic.whichSpecialistsArePartOfThisMDTClinic", "contains-case-insensitive", "Others"],
                  then: "require",
                },
              ],
            },

            // 2.15 Who else should be involved if not already? [open box]
            whoElseShouldBeInvolvedIfNotAlready: {
              type: "textarea",
              label: l(
                "16. Who else should be involved if not already?",
                "16. Qui d'autre devrait être impliqué si ce n'est pas déjà le cas?",
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "not-equals", "Yes"],
                  then: "hide",
                },
                {
                  if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "equals", "Yes"],
                  then: "require",
                },
              ],
            },
            // 2.16 How do you typically communicate with other members of the multidisciplinary team to support the management of patients with advanced NMSC (e.g. scheduled meetings, text, email)? [open box]
            howDoYouTypicallyCommunicateWithOtherMembersOfTheMultidisciplinaryTeamToSupportTheManagementOfPatientsWithAdvancedNMSC:
              {
                type: "textarea",
                label: l(
                  "17. How do you typically communicate with other members of the multidisciplinary team to support the management of patients with advanced NMSC (CSCC/BCC) (e.g. scheduled meetings, text, email)?",
                  "17. Comment communiquez-vous habituellement avec les membres de l'EMD dans le but de soutenir la prise en charge des patients atteints de NMSC (CSCC/BCC) de stade avancé (p. ex., réunions planifiées, textos, courriels)?",
                ),
                schema: z
                  .string({
                    required_error: l("Please enter a value", "Ce champ est requis"),
                    invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                  })
                  .nullish(),
                dependencies: [
                  {
                    if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "not-equals", "Yes"],
                    then: "hide",
                  },
                  {
                    if: ["mdtClinic.doesYourCenterHaveAMDTClinic", "equals", "Yes"],
                    then: "require",
                  },
                ],
              },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        mdtBoardMeeting: {
          type: "object",
          label: "",
          properties: {
            // 2.17 Does your center have a MDT board meeting? [Yes or No] [If selected No, skip to question 2.19]
            // [If selected yes] Which specialists are part of this MDT board meeting?
            // Dermatologists [checkbox]
            // Surgeons [checkbox]
            // Radio oncologists [checkbox]
            // Pathologists [checkbox]
            // Community medical oncologists [checkbox]
            // Others (please specify) [open box]
            doesYourCenterHaveAMDTBoardMeeting: {
              type: "radioGroup",
              label: l(
                "18. Does your center have an MDT board meeting?",
                "18. Votre centre tient-il des réunions de l'EMD?",
              ),
              schema: z.enum(yesNoEnum, {
                errorMap: enumErrorMap(l),
              }),
              mapToLabels: mapToYesNoEnumLabels,
              cols: 2,
            },

            whichSpecialistsArePartOfThisMDTBoardMeeting: {
              type: "checkboxGroup",
              label: l(
                "Which specialists are part of this MDT board meeting?",
                "Quels spécialistes participent à ces réunions de l'EMD?",
              ),
              schema: z
                .array(z.enum(typesOfSpecialistsEnum), {
                  errorMap: enumErrorMap(l),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "not-contains-case-insensitive", "Yes"],
                  then: "hide",
                },

                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "contains-case-insensitive", "Yes"],
                  then: "require",
                },
              ],
              mapToLabels: mapToTypesOfSpecialistsEnumLabels,
            },
            whichSpecialistsArePartOfThisMDTBoardMeetingOther: {
              type: "textarea",
              label: l("Please specify 'Others':", "Autres (veuillez préciser) :"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "not-equals", "Yes"],
                  or: [
                    "mdtBoardMeeting.whichSpecialistsArePartOfThisMDTBoardMeeting",
                    "not-contains-case-insensitive",
                    "Others",
                  ],
                  then: "hide",
                },
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "equals", "Yes"],
                  and: [
                    "mdtBoardMeeting.whichSpecialistsArePartOfThisMDTBoardMeeting",
                    "contains-case-insensitive",
                    "Others",
                  ],
                  then: "require",
                },
              ],
            },
            whoElseShouldBeInvolvedIfNotAlready: {
              type: "textarea",
              label: l(
                "Who else should be involved if not already?",
                "Qui d'autre devrait être impliqué si ce n'est pas déjà le cas?",
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "not-contains-case-insensitive", "Yes"],
                  then: "hide",
                },
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "contains-case-insensitive", "Yes"],
                  then: "require",
                },
              ],
            },
            howFrequentlyAreTheseMDTBoardMeetingsHeld: {
              type: "radioGroup",
              label: l(
                "How frequently are these MDT board meetings held?",
                "À quelle fréquence ces réunions de l'EMD ont-elles lieu?",
              ),
              schema: z
                .enum(typesOfMeetingFrequencyEnum, {
                  errorMap: enumErrorMap(l),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "not-contains-case-insensitive", "Yes"],
                  then: "hide",
                },
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "contains-case-insensitive", "Yes"],
                  then: "require",
                },
              ],
              mapToLabels: mapToTypesOfMeetingFrequencyEnumLabels,
            },
            howFrequentlyAreTheseMDTBoardMeetingsHeldOther: {
              type: "textarea",
              label: l("Other (please specify):", "Autre (veuillez préciser)"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "not-contains-case-insensitive", "Yes"],
                  or: [
                    "mdtBoardMeeting.howFrequentlyAreTheseMDTBoardMeetingsHeld",
                    "not-contains-case-insensitive",
                    "Other",
                  ],
                  then: "hide",
                },
                {
                  if: ["mdtBoardMeeting.doesYourCenterHaveAMDTBoardMeeting", "contains-case-insensitive", "Yes"],
                  and: [
                    "mdtBoardMeeting.howFrequentlyAreTheseMDTBoardMeetingsHeld",
                    "contains-case-insensitive",
                    "Other",
                  ],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 2.19 What are the clinical decision-making factors to determine referral to the following specialists:
        // Dermatologists [open box]
        // Mohs [open box]
        // ENT [open box]
        // Surgical oncologists [open box]
        // Radio oncologist [open box]
        // Medical oncologists [open box]
        whatAreTheClinicalDecisionMakingFactorsToDetermineReferralToTheFollowingSpecialists: {
          type: "object",
          label: l(
            "19. What are the clinical decision-making factors to determine referral to the following specialists:",
            "19. Quels sont les facteurs décisionnels cliniques qui déterminent la référence vers les spécialistes suivants :",
          ),
          properties: {
            dermatologists: {
              type: "textarea",
              label: l("Dermatologists", "Dermatologues"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
            },
            mohs: {
              type: "textarea",
              label: l("Mohs", "Chirurgiens Mohs"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
            },
            ent: {
              type: "textarea",
              label: l("ENT", "ORL"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
            },
            surgicalOncologists: {
              type: "textarea",
              label: l("Surgical oncologists", "Oncologues chirurgicaux"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
            },
            rad_oncologists: {
              type: "textarea",
              label: l("Radiation oncologists", "Radio-oncologues"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
            },
            medicalOncologists: {
              type: "textarea",
              label: l("Medical oncologists", "Oncologues médicaux"),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
            },
          },
        },
      },
    },
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        confirmatoryDoYouHaveAnyAdditionalComments: {
          type: "textarea",
          label: l(
            "Do you have any additional comments, suggestions, or concerns regarding the current version of the schema, particularly in relation to your own personal practice?",
            "Avez-vous d'autres commentaires, suggestions ou préoccupations au sujet de la version actuelle du schéma, particulièrement en ce qui concerne votre pratique personnelle?",
          ),
          schema: z.string().nullish(),
        },
      },
    },
  ] as const satisfies FormConfigInPages;

export const confirmatoryDiagnosisForm: (l: I18NFunction) => SectionConfig = (l) => ({
  sectionId: "confirmatory-diagnosis",
  sectionNumber: 2,
  title: l(
    "Confirmatory Diagnosis, Risk Assessment, and Staging",
    "Diagnostic de confirmation, évaluation du risque et détermination du stade",
  ),
  description: "",
  diagrams: [
    {
      src: l("/diagrams/confirmatory_diagnosis.png", "/diagrams/confirmatory_diagnosis_fr.png"),
      alt: l(
        "Confirmatory Diagnosis, Risk Assessment, and Staging",
        "Diagnostic de confirmation, évaluation du risque et détermination du stade",
      ),
    },
  ],
  formPages: confirmatoryDiagnosisFormConfigInPages(l).map((page) => ({
    formConfig: page.formFields,
    formSchemas: formConfigToZodSchemas(page.formFields, l),
    defaultValues: getFormConfigDefaultValues(page.formFields),
  })),
  // formRootKeys: Object.keys(
  //   Object.assign({}, ...confirmatoryDiagnosisFormConfigInPages.flatMap((page) => page.formFields))
  // ),
  responseKeyToFormConfig: Object.fromEntries(
    confirmatoryDiagnosisFormConfigInPages(l).flatMap((page) =>
      Object.entries(page.formFields).map(([key, field]) => [key, field]),
    ),
  ),
});
